/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';

/**
 * Hook providing functions for current contract and term selection.
 * @returns {{findSelectedTerm, getValuesByPropName, promotionContract}}
 * @param props
 */
export function useContractSelection(props) {
  const {
    contract,
    selectedTerm,
    setAsSelectedContract,
  } = props;
  const { setFieldValue } = useFormikContext();

  /**
   * Finds the term with the specified value in the provided terms array.
   * @param {Array} terms - The array of terms to search.
   * @returns {Object|null} - The found term or null if not found.
   */
  const findSelectedTerm = useCallback((terms) => terms?.find((term) => (term.termValue === selectedTerm)), [selectedTerm]);

  /**
   * Gets the promotion contract for the selected term, if available.
   * @returns {Object|null} - The promotion contract or null if not available.
   */
  const getPromotionContract = useCallback(() => {
    if (contract?.promotions && contract.promotions[selectedTerm]) {
      return contract.promotions[selectedTerm];
    }
    return null;
  }, [contract?.promotions, selectedTerm]);

  // Memoized promotion contract to avoid unnecessary recalculations
  const promotionContract = useMemo(() => getPromotionContract(), [getPromotionContract]);

  /**
   * Gets the term from the selected contract and its associated promotion term, if available.
   * @returns {{origin: Object|null, promotion: Object|null}} - The combined term object.
   */
  const getBundleFromSelectedTerm = useCallback(() => {
    const contractTerm = findSelectedTerm(contract.terms);

    // Initialize promotionTerm as null
    let promotionTerm = null;

    // Check if contract has promotions and promotions have terms
    if (promotionContract) {
      promotionTerm = findSelectedTerm(promotionContract.terms);
    }

    return {
      origin: contractTerm,
      promotion: promotionTerm,
    };
  }, [contract.terms, findSelectedTerm, promotionContract]);

  /**
   * Gets values based on the property name for both the contract and its promotion (if available).
   * @param {string} propName - The name of the property to retrieve.
   * @param {boolean} storedAtContractLevel - Indicates whether the property is stored at the contract level.
   * @returns {{value: any, newValue: any}} - The combined values.
   */
  const getValuesByPropName = useCallback((propName, storedAtContractLevel = false) => {
    let contractValue = null;
    let promotionValue = null;

    if (storedAtContractLevel) {
      contractValue = contract[propName];
      promotionValue = promotionContract ? promotionContract[propName] : null;
    } else {
      const bundleData = getBundleFromSelectedTerm();
      contractValue = bundleData.origin[propName];
      promotionValue = bundleData.promotion ? bundleData.promotion[propName] : null;
    }

    return {
      value: contractValue,
      newValue: promotionValue,
    };
  }, [contract, getBundleFromSelectedTerm, promotionContract]);

  /**
   * used to get values from promotion contract/term or basic contract/term as fallback
   * @type {function(*, boolean=): *|null}
   */
  const getContractOrTermValue = useCallback((propName, storedAtContractLevel = false) => {
    const propertyData = getValuesByPropName(propName, storedAtContractLevel);
    return propertyData.newValue ? propertyData.newValue : propertyData.value;
  }, [getValuesByPropName]);

  /**
   * @param element
   * @returns {boolean}
   */
  const shouldBlockEvent = (element) => {
    // avoid reassign of param (es-lint rule)
    let esLintElement = element;
    // Traverse the parent nodes until the root (document)
    while (esLintElement && esLintElement !== document && !esLintElement.dataset?.stopBlockingEvent) {
      // Check if the current element has the data-block-event attribute
      if (esLintElement.dataset.blockEvent) {
        return true;
      }
      // Move to the next parent node
      esLintElement = esLintElement.parentNode;
    }
    // If no parent has the attribute, return false
    return false;
  };

  /**
   * @param e
   * @returns {boolean}
   */
  const changeContractSelection = (e) => {
    if (shouldBlockEvent(e.target)) return false;

    // Set new_basic_amount price rule to formik context, so we can use ist later
    if (getValuesByPropName('priceAdjustmentRules').newValue) {
      const newBasicAmountRule = getValuesByPropName('priceAdjustmentRules')
        .newValue
        .find((rule) => rule.type === 'NEW_BASIC_AMOUNT');
      setFieldValue('contractPriceAdjustmentRule', newBasicAmountRule);
    } else {
      setFieldValue('contractPriceAdjustmentRule', '');
    }
    if (getValuesByPropName('contractVolumeInformation').value) {
      setFieldValue('contractVolumeInformation', getValuesByPropName('contractVolumeInformation'));
    }

    setAsSelectedContract(
      {
        term: promotionContract ? findSelectedTerm(promotionContract.terms) : findSelectedTerm(contract.terms),
        bundle: promotionContract || contract,
        originRateBundleTerm: findSelectedTerm(contract?.terms),
        promotionRateBundleTerm: findSelectedTerm(promotionContract?.terms),
      },
    );
    return true;
  };

  return {
    findSelectedTerm,
    promotionContract,
    getValuesByPropName,
    getContractOrTermValue,
    changeContractSelection,
  };
}

useContractSelection.propTypes = {
  contract: PropTypes.object.isRequired,
  selectedTerm: PropTypes.number,
  setAsSelectedContract: PropTypes.func.isRequired,
};

useContractSelection.defaultValues = {
  selectedTerm: 12,
};
